import type {
  ContributorFlowPlaylistScope,
  EventMediaSettingsDto,
  FlowEffectiveMediaSettingsDto,
  FlowMediaSettingsCapabilitiesDto,
  FlowMediaSettingsDto,
  MediaPermissionPairDto,
} from '@/types/dtos/media/media-settings.dto';
import { CONTRIBUTOR_FLOW_PLAYLIST_SCOPES } from '@/types/dtos/media/media-settings.dto';

export function defaultEventMediaSettings(): EventMediaSettingsDto {
  return {
    hosts: { allowSelfCreate: true, allowReorder: true, allowFlowOverride: true },
    speakers: { allowFlowOverride: true, allowSessionOverride: true },
  };
}

type HostMediaField = keyof MediaPermissionPairDto;

export function resolveHostEffectiveField(
  eventSettings: EventMediaSettingsDto,
  mediaSettings: FlowMediaSettingsDto | undefined,
  scope: ContributorFlowPlaylistScope | undefined,
  field: HostMediaField,
): boolean {
  const eventValue = eventSettings.hosts[field];

  if (!eventSettings.hosts.allowFlowOverride) {
    return eventValue;
  }

  const flowValue = mediaSettings?.hosts?.[field] ?? eventValue;

  if (!(mediaSettings?.hosts?.allowScopeOverride ?? true)) {
    return flowValue;
  }

  if (!scope) {
    return flowValue;
  }

  const scopePair = mediaSettings?.hosts?.scopes?.[scope];
  if (scopePair?.[field] !== undefined) {
    return scopePair[field]!;
  }

  return flowValue;
}

export function buildEffectiveFlowHostScopes(
  eventSettings: EventMediaSettingsDto,
  mediaSettings: FlowMediaSettingsDto | undefined,
): FlowEffectiveMediaSettingsDto['hosts']['scopes'] {
  return Object.fromEntries(
    CONTRIBUTOR_FLOW_PLAYLIST_SCOPES.map((scope) => [
      scope,
      {
        allowSelfCreate: resolveHostEffectiveField(eventSettings, mediaSettings, scope, 'allowSelfCreate'),
        allowReorder: resolveHostEffectiveField(eventSettings, mediaSettings, scope, 'allowReorder'),
      },
    ]),
  ) as FlowEffectiveMediaSettingsDto['hosts']['scopes'];
}

export function buildFlowEffectiveMediaSettings(
  eventSettings: EventMediaSettingsDto,
  mediaSettings: FlowMediaSettingsDto | undefined,
): FlowEffectiveMediaSettingsDto {
  return {
    hosts: {
      allowSelfCreate: resolveHostEffectiveField(eventSettings, mediaSettings, undefined, 'allowSelfCreate'),
      allowReorder: resolveHostEffectiveField(eventSettings, mediaSettings, undefined, 'allowReorder'),
      allowFlowOverride: eventSettings.hosts.allowFlowOverride,
      scopes: buildEffectiveFlowHostScopes(eventSettings, mediaSettings),
    },
    speakers: {
      allowSessionOverride: mediaSettings?.speakers?.allowSessionOverride ?? true,
    },
  };
}

export function buildFlowMediaSettingsCapabilities(
  eventSettings: EventMediaSettingsDto,
  mediaSettings: FlowMediaSettingsDto | undefined,
  existingCaps?: FlowMediaSettingsCapabilitiesDto,
): FlowMediaSettingsCapabilitiesDto {
  const canEdit = existingCaps?.canEdit ?? false;
  const canEditFlowHostDefaults = canEdit && eventSettings.hosts.allowFlowOverride;
  const canEditScopeOverride = canEdit && eventSettings.hosts.allowFlowOverride;
  const canEditSessionOverride = canEdit && eventSettings.speakers.allowFlowOverride;
  const allowScopeOverride = mediaSettings?.hosts?.allowScopeOverride ?? true;
  const canEditScopePlaylistSettings = canEditScopeOverride && allowScopeOverride;

  const scopes = Object.fromEntries(
    CONTRIBUTOR_FLOW_PLAYLIST_SCOPES.map((scope) => [scope, { canEdit: canEditScopePlaylistSettings }]),
  ) as FlowMediaSettingsCapabilitiesDto['scopes'];

  return {
    canEdit,
    canEditFlowHostDefaults,
    canEditScopeOverride,
    canEditSessionOverride,
    scopes,
  };
}
